/** Public, non-sensitive summary of the machine agent's own heartbeat. */
export interface CduAgentReport {
  registered: boolean;
  version: string | null;
  last_seen: number | null;
  online_until: number | null;
}

export interface AgentHostReport { cdu_agent?: CduAgentReport | null }

const hostKey = (name: unknown) => {
  if (typeof name !== 'string' || !name.trim()) return null;
  const normalized = name.trim().toLowerCase();
  const numbered = /^(?:srv|server)(\d+)(?:\.[a-z0-9.-]+)?$/.exec(normalized);
  return numbered ? `server${numbered[1].padStart(2, '0')}` : normalized;
};

/** Do not attach another machine's status when aliases conflict. */
export function agentForHost(hosts: Record<string, AgentHostReport>, hostname: unknown): CduAgentReport | null {
  const key = hostKey(hostname);
  if (!key) return null;
  const matches = Object.entries(hosts).filter(([name]) => hostKey(name) === key);
  return matches.length === 1 ? matches[0][1]?.cdu_agent ?? null : null;
}

export function cduAgentState(report: CduAgentReport | null | undefined, now: number) {
  const version = typeof report?.version === 'string' && report.version.trim() ? report.version.trim().slice(0, 100) : null;
  const unknown = { label: 'Unavailable', tone: 'muted', version, lastSeen: null as number | null,
    detail: 'Agent heartbeat data is unavailable.' };
  if (!report || typeof report.registered !== 'boolean') return unknown;
  if (!report.registered) return { ...unknown, label: 'Not reported', version: null, detail: 'No CDU Agent heartbeat has been reported for this host.' };
  if (report.last_seen === null && report.online_until === null) return { ...unknown, label: 'Not reported', detail: 'The registered CDU Agent has not reported a heartbeat.' };
  const seen = report.last_seen, until = report.online_until;
  if (typeof seen !== 'number' || !Number.isFinite(seen) || seen <= 0 ||
      typeof until !== 'number' || !Number.isFinite(until) || until <= seen || until > seen + 90 ||
      !Number.isFinite(now) || seen * 1000 > now + 30_000) return unknown;
  const online = now < until * 1000;
  return { label: online ? 'Online' : 'Offline', tone: online ? 'good' : 'danger', version, lastSeen: seen,
    detail: `${online ? 'Heartbeat received' : 'No current heartbeat'}; last contact ${new Date(seen * 1000).toLocaleString()}. Offline after 90 seconds without a heartbeat.` };
}
